import { useEffect } from 'react';

// SEO configuration for each page
const seoConfig = {
  home: {
    title: 'Nicola Scerni | Consulente Marketing Strategico per Aziende Ambiziose',
    description: 'Consulenza di Marketing Strategico per imprenditori ambiziosi. +14 anni di esperienza, €2.5M+ generati per i clienti. Strategie su misura per dominare il tuo mercato.',
    keywords: 'consulente marketing, marketing strategico, strategia aziendale, consulenza PMI, Nicola Scerni',
    canonical: 'https://nicolascerni.it/',
  },
  chiSono: {
    title: 'Chi Sono | Nicola Scerni - Consulente Marketing Strategico',
    description: 'Scopri chi è Nicola Scerni: +14 anni di esperienza nel marketing strategico, €2.5M+ generati per i clienti. Un approccio pragmatico per risultati misurabili.',
    keywords: 'chi è Nicola Scerni, consulente marketing, esperienza marketing strategico, professionista marketing',
    canonical: 'https://nicolascerni.it/chi-sono',
  },
  metodo: {
    title: 'Metodo D.O.M.I.N.A. | Framework di Marketing Strategico - Nicola Scerni',
    description: 'Il metodo D.O.M.I.N.A.: un framework proprietario di marketing strategico in 6 fasi per trasformare la tua azienda e dominare il mercato.',
    keywords: 'metodo DOMINA, framework marketing, strategia marketing, consulenza strategica, processo marketing',
    canonical: 'https://nicolascerni.it/metodo',
  },
  servizi: {
    title: 'Servizi di Consulenza Marketing Strategico | Nicola Scerni',
    description: 'Servizi di consulenza marketing strategico: analisi di mercato, strategia di posizionamento, piano marketing, ottimizzazione conversioni. Soluzioni su misura.',
    keywords: 'servizi consulenza marketing, strategia marketing, consulenza aziendale, piano marketing, analisi mercato',
    canonical: 'https://nicolascerni.it/servizi',
  },
  risultati: {
    title: 'Risultati e Case Study | Nicola Scerni - Consulente Marketing',
    description: 'Scopri i risultati ottenuti dai clienti di Nicola Scerni: +180% fatturato, ROI straordinario, strategie che funzionano. Case study reali e testimonianze.',
    keywords: 'risultati marketing, case study, testimonianze clienti, ROI marketing, successi consulenza',
    canonical: 'https://nicolascerni.it/risultati',
  },
  insight: {
    title: 'Insight e Articoli | Blog Marketing Strategico - Nicola Scerni',
    description: 'Articoli, guide e insight sul marketing strategico. Consigli pratici per imprenditori che vogliono far crescere la propria azienda.',
    keywords: 'blog marketing, articoli strategia, insight business, guide imprenditori, contenuti marketing',
    canonical: 'https://nicolascerni.it/insight',
  },
  privacy: {
    title: 'Privacy Policy | Nicola Scerni',
    description: 'Informativa sulla privacy e trattamento dei dati personali del sito nicolascerni.it',
    keywords: 'privacy policy, GDPR, trattamento dati',
    canonical: 'https://nicolascerni.it/privacy-policy',
  },
  cookie: {
    title: 'Cookie Policy | Nicola Scerni',
    description: 'Informativa sui cookie utilizzati dal sito nicolascerni.it',
    keywords: 'cookie policy, cookies, informativa cookie',
    canonical: 'https://nicolascerni.it/cookie-policy',
  },
};

const SEOHead = ({ page, customTitle, customDescription, article }) => {
  const config = seoConfig[page] || seoConfig.home;
  
  const title = customTitle || config.title;
  const description = customDescription || config.description;

  useEffect(() => {
    document.title = title;
    
    // Meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
    
    // Meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', article ? `${article.category || ''}, marketing strategico, ${article.title}` : config.keywords);
    }
    
    // Canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      const canonicalUrl = article ? `https://nicolascerni.it/insight/${article.id}` : config.canonical;
      canonicalLink.setAttribute('href', canonicalUrl);
    }
    
    // OG tags
    const ogImage = article?.imageUrl || article?.image_url || article?.image || 'https://nicolascerni.it/og-image.png';
    const ogTags = {
      'og:title': title,
      'og:description': description,
      'og:url': article ? `https://nicolascerni.it/insight/${article.id}` : config.canonical,
      'og:image': ogImage,
      'og:type': article ? 'article' : 'website',
    };
    
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      }
    });

    // Twitter image
    let twitterImage = document.querySelector('meta[property="twitter:image"]');
    if (twitterImage) {
      twitterImage.setAttribute('content', ogImage);
    }
    let twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', title);
    }
    let twitterDesc = document.querySelector('meta[property="twitter:description"]');
    if (twitterDesc) {
      twitterDesc.setAttribute('content', description);
    }
    
    // Schema.org Article for blog posts
    let existingArticleSchema = document.querySelector('script[data-schema="article"]');
    if (article) {
      const articleSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.excerpt || description,
        "image": ogImage,
        "author": {
          "@type": "Person",
          "name": "Nicola Scerni",
          "url": "https://nicolascerni.it/chi-sono"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Nicola Scerni",
          "logo": { "@type": "ImageObject", "url": "https://nicolascerni.it/favicon.png" }
        },
        "datePublished": article.created_at || new Date().toISOString(),
        "dateModified": article.updated_at || article.created_at || new Date().toISOString(),
        "mainEntityOfPage": `https://nicolascerni.it/insight/${article.id}`
      };
      
      if (!existingArticleSchema) {
        existingArticleSchema = document.createElement('script');
        existingArticleSchema.type = 'application/ld+json';
        existingArticleSchema.setAttribute('data-schema', 'article');
        document.head.appendChild(existingArticleSchema);
      }
      existingArticleSchema.textContent = JSON.stringify(articleSchema);
    } else if (existingArticleSchema) {
      existingArticleSchema.remove();
    }
    
  }, [title, description, config, article]);

  return null;
};

export default SEOHead;
export { seoConfig };
